import { clearBatchEntries } from '~/actions/orders.actions';
import { AxiosResponse } from 'axios';
import { replace } from 'connected-react-router';
import uniq from 'lodash-es/uniq';
import { call, put, select } from 'redux-saga/effects';
import {
  getBatchEntriesByIds,
  getBatchEntriesByOrderObjectIds,
} from '~/selectors/batch-entry.selectors';
import { getPathname, getQS } from '~/selectors/router.selectors';
import { getOrCloneBatchEntriesForOrders } from '~/services/orders.service';
import { OrderSelection } from '~/types/Order';
import {
  BatchEntryExtended,
  batchEntryExtendedFromJSON,
  JSONBatchEntryExtended,
} from '~/types/OrderBatchEntry';
import { BatchEntryId } from '~/types/OrdersBatch';
import { selectState } from '..';

export function* getBatchEntriesIdsToUpdate(orderSelection: OrderSelection) {
  const {
    batchEntries,
    resetBatchEntries,
    newSelectedOrder,
  }: {
    batchEntries: BatchEntryExtended[];
    resetBatchEntries: BatchEntryExtended[];
    newSelectedOrder: OrderSelection;
  } = yield call(setupBatchEntries, orderSelection);
  const batchEntriesIdsToUpdate = uniq(batchEntries.map((be) => be.id));
  return { batchEntriesIdsToUpdate, resetBatchEntries, newSelectedOrder };
}

export function* setupBatchEntries(orderSelection: OrderSelection) {
  const orderObjectIDs: string[] = [];
  const batchEntryIDs: BatchEntryId[] = [];

  Object.entries(orderSelection).forEach(([orderObjectId, orderBatchEntryIDs]) => {
    orderObjectIDs.push(orderObjectId);
    if (orderBatchEntryIDs?.length > 0) {
      batchEntryIDs.push(...orderBatchEntryIDs);
    }
  });

  const getOrCloneResult: AxiosResponse<JSONBatchEntryExtended[]> = yield call(
    getOrCloneBatchEntriesForOrders,
    orderObjectIDs,
    batchEntryIDs,
  );
  const batchEntries: BatchEntryExtended[] =
    getOrCloneResult?.data.map(batchEntryExtendedFromJSON) || [];

  // This is a single order that didn't have a batch entry before and now does, or it was already
  // purchased and the user made an edit that results in a new batch entry.
  // And if the user is on the order screen, we need to update the url
  if (orderObjectIDs.length === 1 && batchEntries.length === 1) {
    const orderObjectID = orderObjectIDs[0];
    const beId = batchEntries[0].id;
    const pathname: string = yield selectState<string>(getPathname);
    if (pathname.includes(orderObjectID) && !pathname.includes(beId.toString())) {
      const qs: string | undefined = yield selectState<string | undefined>(getQS);
      yield put(replace(`/orders/${orderObjectID}/shipments/${beId}${qs}`));
    }
  }

  // TODO: this sucks, find a better way
  //   may not need initialBatchEntries; batch entry IDs in orderSelection might be enough
  //   might modify the API endpoint to return resetBatchEntries directly instead of calculating here
  const initialBatchEntries: BatchEntryExtended[] = yield call(
    orderSelectionToBatchEntries,
    orderSelection,
  );
  const resetBatchEntries = findResetBatchEntries(initialBatchEntries, batchEntries);

  yield put(clearBatchEntries(initialBatchEntries, resetBatchEntries));

  // These are the selected orders with updated batch entry ids
  const newSelectedOrder: OrderSelection = batchEntries.reduce((accum, entry) => {
    accum[entry.orderObjectId] = accum[entry.orderObjectId]
      ? [...accum[entry.orderObjectId], entry.id]
      : [entry.id];
    return accum;
  }, {});

  return { batchEntries, resetBatchEntries, newSelectedOrder };
}

// Generates a list of batch entries from the given orderSelection.  This will include the specified batch entries
// included in the orderSelection, plus all batch entries for orders which had an empty array of batch entries in
// the order selection.  (These correspond to the batch entries on which the API call operates.)
export function* orderSelectionToBatchEntries(orderSelection: OrderSelection) {
  const [orderObjectIds, batchEntryIds] = Object.entries(orderSelection).reduce(
    ([os, bes], [orderObjectId, batchEntryIds]) =>
      batchEntryIds?.length ? [os, bes.concat(batchEntryIds)] : [os.concat(orderObjectId), bes],
    [new Array<string>(), new Array<number>()],
  );
  const batchEntriesFromOrders: BatchEntryExtended[] = yield select(
    getBatchEntriesByOrderObjectIds,
    { orderObjectIds },
  );
  const batchEntriesFromIds: BatchEntryExtended[] = yield select(getBatchEntriesByIds, {
    batchEntryIds,
  });
  return batchEntriesFromOrders.concat(batchEntriesFromIds);
}

// returns batch entries that were in the result but not the request, indicating they were created
// or regenerated by the API call
export function findResetBatchEntries(
  initialBatchEntries: BatchEntryExtended[],
  batchEntries: BatchEntryExtended[],
) {
  const batchEntryIdsSelected = new Set<number>(
    initialBatchEntries.map((batchEntry) => batchEntry.id),
  );
  return batchEntries.filter((batchEntry) => !batchEntryIdsSelected.has(batchEntry.id));
}
